import { ClickableProps } from "@insite/mobius/Clickable";
import { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import { HiddenProps } from "@insite/mobius/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { GridItemProps } from "@store/MobiusOverrides/GridItem/GridItem";
import { contentSpacingStyles, gridGapStyles, linkStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import Color from "color";
import { css } from "styled-components";

export interface StoreTableGridProps {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    countText?: TypographyPresentationProps;
    mobileSortSelectHidden?: HiddenProps;
    mobileSortSelect?: SelectPresentationProps;
    tableGridHead?: GridContainerProps;
    tableGridHeader?: GridItemProps;
    clickableHeader?: ClickableProps;
    clickableHeaderIcon?: IconPresentationProps;
    textHeader?: TypographyProps;
    tableGridBody?: GridContainerProps;
    tableGridRow?: GridItemProps;
    tableGridRowContainer?: GridContainerProps;
    tableGridCell?: GridItemProps;
    tableGridCellText?: TypographyPresentationProps;
    mobileHeaderHidden?: HiddenProps;
    mobileHeader?: TypographyPresentationProps;
    greenStatusTag?: InjectableCss;
    orangeStatusTag?: InjectableCss;
}

const statusTagStyles = css`
    width: fit-content;
    padding: 0.15em 1em;
    border-radius: 0.75em;
    font-weight: ${TYPOGRAPHY.weight.bold};
    font-size: ${TYPOGRAPHY.size.tiny};
`;

export const tableGridProps: StoreTableGridProps = {
    container: {
        css: css`
            width: 100%;
            margin-bottom: 50px;
        `,
    },
    centeringWrapper:  {
        css: css`
            height: 150px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 0 1em;
        `,
    },
    noResultsText: {
        forwardAs: "p",
        variant: "h6",
    },
    countText: {
        forwardAs: "p",
        size: TYPOGRAPHY.size.smaller,
        css: css`
            ${contentSpacingStyles()}
        `,
    },
    mobileSortSelectHidden: {
        above: "md",
        css: css`
            width: 100%;
            text-align: right;
            margin: 1em auto 2em;
        `,
    },
    mobileSortSelect: {
        labelProps: {
            css: css`
                width: unset;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 100%;
                max-width: 500px;
                justify-content: end;
            `,
        },
    },
    tableGridHead: {
        gap: 0,
        css: css`
            ${gridGapStyles(["22px"], ["22px"])}

            ${GridOffset} {
                margin: 0;
            }
        `,
    },
    tableGridHeader: {
        width: [0, 0, 0, 2, 2],
        css: css`
            background-color: ${getColor("secondary")};
        `,
    },
    clickableHeader: {
        css: css`
            width: 100%;
            justify-content: space-between;
            color: ${getColor("secondary.contrast")};
            font-weight: ${TYPOGRAPHY.weight.bold};

            &:hover {
                text-decoration: underline;
            }
        `,
    },
    clickableHeaderIcon: {
        size: 15,
    },
    textHeader: {
        css: css`
            color: ${getColor("secondary.contrast")};
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    tableGridBody: {
        gap: 0,
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            ${gridGapStyles([], ["36px", null, null, 0])}

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, null, TYPOGRAPHY.size.smaller], properties: ["font-size"] }],
                    mediaOptions.min,
                )}

        `,
    },
    tableGridRow: {
        width: 12,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, null, "none"], properties: ["border-bottom"] }],
                    mediaOptions.min,
                )}

            &:nth-child(even) {
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [
                            { values: [null, null, null, Color(props.theme.colors.common.accent).string()], properties: ["background-color"] }, // breakpointValuesMediaQueries doesn't work with getColor("common.accent")
                        ], 
                        mediaOptions.min,
                    )}
            }

            &:first-of-type {
                > ${GridWrapper} {
                    ${props =>
                        breakpointValuesMediaQueries(
                            props.theme,
                            [{ values: ["16px", null, null, "0px"], properties: ["margin-top"] }],
                            mediaOptions.min,
                        )}
                }
            }
        `,
    },
    tableGridRowContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(
                ["24px", null, null, "22px"], 
                ["10px", null, null, "22px"]
            )}

            ${GridOffset} {
                margin: 0;
            }
        `,
    },
    tableGridCell: {
        width: [6, 6, 6, 2, 2],
        css: css`
            flex-direction: row;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, "center"], properties: ["align-items"] }],
                    mediaOptions.max,
                )}
        `,
    },
    tableGridCellText: {
        css: css`
            width: 100%;
            word-wrap: break-word;
        `,
    },
    mobileHeaderHidden: {
        above: "md",
    },
    mobileHeader: {
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            white-space: pre;
        `,
    },
    greenStatusTag: {
        css: css`
            ${statusTagStyles}
            background-color: ${getColor("success.light")};
            color: ${getColor("success.main")};
        `,
    },
    orangeStatusTag: {
        css: css`
            ${statusTagStyles}
            ${props => {
                return css`
                    background-color: ${Color(props.theme.colors.warning.main).lighten(0.55).hex()};
                    color: ${Color(props.theme.colors.warning.main).darken(0.25).hex()};
                `;
            }}
        `,
    },
};

/* Example of how to build a component to use these styles: \src\FrontEnd\modules\blueprints\store\src\Overrides\Widgets\OrderHistory\OrderHistoryTable.tsx */